/**
 * Label Layout Engine
 *
 * A declarative layout system for label printing that automatically handles positioning
 * and spacing. Components are arranged in stacks and grids without manual coordinate calculation.
 *
 * ## Key Features
 * - Automatic positioning (no manual x,y coordinates)
 * - Serializable layout definitions (JSON)
 * - Responsive to different label sizes
 * - Familiar layout concepts (VStack, HStack, Spacer)
 * - Type-safe layout definitions
 *
 * ## Usage Examples
 *
 * ```typescript
 * const specimenLayout: LayoutDefinition = {
 *   type: "vstack",
 *   spacing: 4,
 *   padding: 8,
 *   children: [
 *     {
 *       type: "hstack",
 *       spacing: "auto", // pushes content to edges
 *       children: [
 *         { type: "text", content: "{{caseId}}", fontSize: 20 },
 *         { type: "text", content: "{{tubeName}}", fontSize: 20 }
 *       ]
 *     },
 *     {
 *       type: "hstack",
 *       spacing: "auto",
 *       children: [
 *         { type: "text", content: "{{unosId}}", fontSize: 20 },
 *         { type: "text", content: "{{collection}}", fontSize: 20 }
 *       ]
 *     },
 *     { type: "spacer", minHeight: 10 },
 *     {
 *       type: "hstack",
 *       spacing: 20,
 *       alignment: "bottom",
 *       children: [
 *         { type: "barcode", content: "{{accessionId}}", height: 40 },
 *         { type: "text", content: "{{accessionId}}", fontSize: 24, fontFamily: "D" }
 *       ]
 *     }
 *   ]
 * };
 * ```
 */
/**
 * Label Layout Engine
 *
 * A declarative layout system for label printing that automatically handles positioning
 * and spacing. Components are arranged in stacks and grids without manual coordinate calculation.
 *
 * ## Key Features
 * - Automatic positioning (no manual x,y coordinates)
 * - Serializable layout definitions (JSON)
 * - Responsive to different label sizes
 * - Familiar layout concepts (VStack, HStack, Spacer)
 * - Type-safe layout definitions
 *
 * ## Usage Examples
 *
 * ```typescript
 * const specimenLayout: LayoutDefinition = {
 *   type: "vstack",
 *   spacing: 4,
 *   padding: 8,
 *   children: [
 *     {
 *       type: "hstack",
 *       spacing: "auto", // pushes content to edges
 *       children: [
 *         { type: "text", content: "{{caseId}}", fontSize: 20 },
 *         { type: "text", content: "{{tubeName}}", fontSize: 20 }
 *       ]
 *     },
 *     {
 *       type: "hstack",
 *       spacing: "auto",
 *       children: [
 *         { type: "text", content: "{{unosId}}", fontSize: 20 },
 *         { type: "text", content: "{{collection}}", fontSize: 20 }
 *       ]
 *     },
 *     { type: "spacer", minHeight: 10 },
 *     {
 *       type: "hstack",
 *       spacing: 20,
 *       alignment: "bottom",
 *       children: [
 *         { type: "barcode", content: "{{accessionId}}", height: 40 },
 *         { type: "text", content: "{{accessionId}}", fontSize: 24, fontFamily: "D" }
 *       ]
 *     }
 *   ]
 * };
 * ```
 */

import type { Decimal } from "decimal.js";

import type {
  LabelField,
  LabelSettings,
} from "@/features/label-designer/types";
import { calculateTextDimensions } from "@/features/label-designer/utils/textUtils";
import { generateZPL } from "@/features/label-designer/utils/zplGenerator";
import type { LabelSample } from "./types";

// ============================================================================
// Layout Type Definitions
// ============================================================================

/**
 * Border style configuration
 */
interface BorderStyle {
  width?: number; // Border thickness in dots
  color?: "black" | "white"; // ZPL only supports black or white
  style?: "solid" | "dashed"; // Future: ZPL doesn't natively support dashed, but we can simulate
}

/**
 * Base layout component
 */
interface BaseLayoutComponent {
  id?: string;
  hidden?: boolean;
  removeWhenEmpty?: boolean; // If true, component is completely removed when empty
  border?: BorderStyle | boolean; // true = default border, object = custom border
}

/**
 * Text component
 */
interface TextComponent extends BaseLayoutComponent {
  type: "text";
  content: string;
  fontSize?: number;
  fontFamily?: string;
  alignment?: "left" | "center" | "right";
  truncate?: boolean;
  maxWidth?: number;
  /** Enable multi-line text wrapping */
  multiline?: boolean;
  /** Maximum number of lines (0 = unlimited) */
  maxLines?: number;
  /** Line height multiplier (default: 1.2) */
  lineHeight?: number;
}

/**
 * Barcode component
 */
interface BarcodeComponent extends BaseLayoutComponent {
  type: "barcode";
  content: string;
  height?: number;
  barcodeType?: string;
  showText?: boolean;
  barWidth?: number;
}

/**
 * Spacer component for flexible spacing
 */
interface SpacerComponent extends BaseLayoutComponent {
  type: "spacer";
  minWidth?: number;
  minHeight?: number;
}

/**
 * Vertical stack container
 */
interface VStackComponent extends BaseLayoutComponent {
  type: "vstack";
  spacing?: number | "auto";
  alignment?: "leading" | "center" | "trailing";
  padding?:
    | number
    | { top?: number; bottom?: number; left?: number; right?: number };
  children: LayoutComponent[];
}

/**
 * Horizontal stack container
 */
interface HStackComponent extends BaseLayoutComponent {
  type: "hstack";
  spacing?: number | "auto";
  alignment?: "top" | "center" | "bottom";
  padding?:
    | number
    | { top?: number; bottom?: number; left?: number; right?: number };
  children: LayoutComponent[];
}

/**
 * Union of all layout components
 */
type LayoutComponent =
  | TextComponent
  | BarcodeComponent
  | SpacerComponent
  | VStackComponent
  | HStackComponent;

/**
 * Complete layout definition for a label
 */
interface LayoutDefinition extends VStackComponent {
  version?: string; // For future compatibility
  labelType?: string; // e.g., "specimen", "archive"
}

// ============================================================================
// Layout Calculation Types
// ============================================================================

interface CalculatedBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LayoutContext {
  availableWidth: number;
  availableHeight: number;
  currentX: number;
  currentY: number;
  labelSettings: LabelSettings;
}

interface RenderedComponent {
  component: LayoutComponent;
  bounds: CalculatedBounds;
  labelField?: LabelField;
}

// ============================================================================
// Template Variable Support
// ============================================================================

/**
 * Replace template variables in content strings
 */
function interpolateContent(
  content: string,
  variables: Record<string, string>,
): string {
  return content.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const value = variables[key];
    // If variable exists and is truthy, use it; otherwise remove the template entirely
    return value ? value : "";
  });
}

/**
 * Apply template variables to a layout component
 */
function interpolateComponent(
  component: LayoutComponent,
  variables: Record<string, string>,
): LayoutComponent {
  if (component.type === "text") {
    return {
      ...component,
      content: interpolateContent(component.content, variables),
    };
  }

  if (component.type === "barcode") {
    return {
      ...component,
      content: interpolateContent(component.content, variables),
    };
  }

  if (component.type === "vstack" || component.type === "hstack") {
    return {
      ...component,
      children: component.children.map((child) =>
        interpolateComponent(child, variables),
      ),
    };
  }

  // For spacer and unknown types, return as-is
  return component;
}

// ============================================================================
// Layout Calculation Engine
// ============================================================================

/**
 * Calculate text dimensions with support for multi-line text
 */
function calculateTextSize(
  text: string,
  fontSize: number,
  fontFamily = "0",
  maxWidth?: number,
  multiline = false,
  maxLines = 0,
  lineHeightMultiplier = 1.2,
): { width: number; height: number; lines: number } {
  // If text is empty, return minimal size but still take up some space
  if (!text || text.trim().length === 0) {
    return {
      width: 0, // Zero width for empty text so it doesn't affect auto spacing
      height: fontSize * lineHeightMultiplier, // Still maintain line height for consistent layout
      lines: 1,
    };
  }

  // If multiline is disabled, treat as single line
  if (!multiline) {
    // For single line, use simplified calculation
    const charWidth = fontSize * 0.6; // Approximate character width
    const lineHeight = fontSize * lineHeightMultiplier;

    // If maxWidth is provided, respect it (important for truncation)
    const textWidth = text.length * charWidth;
    const actualWidth = maxWidth ? Math.min(textWidth, maxWidth) : textWidth;

    return {
      width: actualWidth,
      height: lineHeight,
      lines: 1,
    };
  }

  // If no maxWidth provided for multiline, fall back to simple calculation
  if (!maxWidth) {
    const charWidth = fontSize * 0.6;
    const lineHeight = fontSize * lineHeightMultiplier;

    return {
      width: text.length * charWidth,
      height: lineHeight,
      lines: 1,
    };
  }

  // Use proper text calculation for multi-line text
  const textCalc = calculateTextDimensions(
    text,
    fontSize,
    fontFamily,
    maxWidth,
  );

  // Apply maxLines constraint if specified
  const actualLines =
    maxLines > 0
      ? Math.min(textCalc.numberOfLines, maxLines)
      : textCalc.numberOfLines;

  return {
    width: Math.min(textCalc.widthInDots, maxWidth),
    height: actualLines * fontSize * lineHeightMultiplier,
    lines: actualLines,
  };
}

/**
 * Calculate barcode dimensions (simplified)
 */
function calculateBarcodeSize(
  content: string,
  height = 40,
): { width: number; height: number } {
  // Simplified calculation - actual implementation would use barcode calculation utilities
  const minWidth = content.length * 8; // Rough estimate
  return {
    width: Math.max(minWidth, 100),
    height: height + height * 0.3, // Add space for text if shown
  };
}

/**
 * Calculate the natural size of a layout component
 */
function calculateComponentSize(
  component: LayoutComponent,
  context: LayoutContext,
): { width: number; height: number } {
  switch (component.type) {
    case "text":
      return calculateTextSize(
        component.content,
        component.fontSize || 20,
        component.fontFamily || "0",
        component.maxWidth,
        component.multiline || false,
        component.maxLines || 0,
        component.lineHeight || 1.2,
      );

    case "barcode":
      return calculateBarcodeSize(component.content, component.height);

    case "spacer":
      return {
        width: component.minWidth || 0,
        height: component.minHeight || 0,
      };

    case "vstack":
    case "hstack":
      return calculateStackSize(component, context);

    default:
      return { width: 0, height: 0 };
  }
}

/**
 * Calculate the size of a stack container
 */
function calculateStackSize(
  stack: VStackComponent | HStackComponent,
  context: LayoutContext,
): { width: number; height: number } {
  const padding =
    typeof stack.padding === "number"
      ? {
          top: stack.padding,
          bottom: stack.padding,
          left: stack.padding,
          right: stack.padding,
        }
      : stack.padding || {};

  let totalWidth = (padding.left || 0) + (padding.right || 0);
  let totalHeight = (padding.top || 0) + (padding.bottom || 0);

  // All children should already be filtered at this point
  const childSizes = stack.children.map((child) =>
    calculateComponentSize(child, context),
  );

  if (stack.type === "vstack") {
    // Vertical stack: width = max child width, height = sum of child heights + spacing
    totalWidth += Math.max(...childSizes.map((s) => s.width));
    totalHeight += childSizes.reduce((sum, s) => sum + s.height, 0);

    if (typeof stack.spacing === "number" && stack.children.length > 1) {
      totalHeight += stack.spacing * (stack.children.length - 1);
    }
  } else {
    // Horizontal stack
    if (stack.spacing === "auto") {
      // For auto spacing, claim the full available width
      totalWidth =
        context.availableWidth - (padding.left || 0) - (padding.right || 0);
    } else {
      // Normal spacing: width = sum of child widths + spacing
      totalWidth += childSizes.reduce((sum, s) => sum + s.width, 0);
      if (typeof stack.spacing === "number" && stack.children.length > 1) {
        totalWidth += stack.spacing * (stack.children.length - 1);
      }
    }
    // Height is always max child height
    totalHeight += Math.max(...childSizes.map((s) => s.height));
  }

  return { width: totalWidth, height: totalHeight };
}

/**
 * Create border fields for a component
 */
function createBorderFields(
  component: LayoutComponent,
  bounds: CalculatedBounds,
): LabelField[] {
  if (!component.border) {
    return [];
  }

  // Parse border configuration
  const border: BorderStyle =
    typeof component.border === "boolean"
      ? { width: 1, color: "black" } // Default border
      : component.border;

  const borderWidth = border.width || 1;
  const borderColor = border.color || "black";

  // Create a single box field for the border
  const borderField: LabelField = {
    id: `${component.id || "component"}_border_${Math.random().toString(36).substr(2, 9)}`,
    type: "box",
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    content: "", // Box fields don't need content
    borderWidth,
    borderColor,
  };

  return [borderField];
}

/**
 * Layout a component and its children, calculating absolute positions
 */
function layoutComponent(
  component: LayoutComponent,
  bounds: CalculatedBounds,
  context: LayoutContext,
): RenderedComponent[] {
  const rendered: RenderedComponent[] = [];

  if (component.hidden) {
    return rendered;
  }

  // Don't render empty text components as label fields (but they still affect layout)
  if (
    component.type === "text" &&
    (!component.content || component.content.trim() === "")
  ) {
    return rendered;
  }

  if (component.type === "text" || component.type === "barcode") {
    // Leaf components become label fields
    const labelField: LabelField = {
      id:
        component.id ||
        `${component.type}_${Math.random().toString(36).substr(2, 9)}`,
      type: component.type,
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
      content: component.content,
      ...(component.type === "text" && {
        fontSize: component.fontSize || 20,
        fontFamily: component.fontFamily || "0",
        alignment:
          component.alignment === "center"
            ? "C"
            : component.alignment === "right"
              ? "R"
              : "L",
        truncate: component.truncate || false,
        multiline: component.multiline || false,
        maxLines: component.maxLines || 0,
        lineHeight: component.lineHeight || 1.2,
      }),
      ...(component.type === "barcode" && {
        barcodeType: component.barcodeType || "BCN",
        barcodeHeight: component.height || 40,
        barWidth: component.barWidth,
        showText: component.showText,
      }),
    };

    rendered.push({
      component,
      bounds,
      labelField,
    });
  } else if (component.type === "spacer") {
    // Spacers don't render but take up space
    rendered.push({
      component,
      bounds,
    });
  } else if (component.type === "vstack" || component.type === "hstack") {
    // Stack containers layout their children
    rendered.push(...layoutStack(component, bounds, context));
  }

  // Add border fields for any component that has a border
  const borderFields = createBorderFields(component, bounds);
  for (const borderField of borderFields) {
    rendered.push({
      component,
      bounds,
      labelField: borderField,
    });
  }

  return rendered;
}

/**
 * Filter out empty components from a layout (after interpolation)
 */
function filterEmptyComponents(
  component: LayoutComponent,
): LayoutComponent | null {
  if (component.hidden) return null;

  if (component.type === "text") {
    // If removeWhenEmpty is true and content is empty, remove completely
    if (component.removeWhenEmpty && component.content.trim() === "") {
      return null;
    }
    // Otherwise keep empty text components (they'll render with zero width)
    return component;
  }

  if (component.type === "barcode") {
    // Remove empty barcodes completely as they can't have zero width
    return component.content.trim() === "" ? null : component;
  }

  if (component.type === "spacer") {
    return component; // Spacers are always kept
  }

  if (component.type === "vstack" || component.type === "hstack") {
    // Filter children recursively
    const filteredChildren = component.children
      .map((child) => filterEmptyComponents(child))
      .filter((child): child is LayoutComponent => child !== null);

    // For hstacks with auto spacing, preserve structure even with empty text
    if (component.spacing === "auto" && component.type === "hstack") {
      // Keep the original structure to preserve auto spacing behavior
      const preservedChildren = component.children
        .map((child) => filterEmptyComponents(child))
        .filter((child): child is LayoutComponent => child !== null);

      if (preservedChildren.length === 0) {
        return null;
      }

      return {
        ...component,
        children: preservedChildren,
      };
    }

    // If no visible children, return null to remove this container
    if (filteredChildren.length === 0) {
      return null;
    }

    // Return the container with filtered children
    return {
      ...component,
      children: filteredChildren,
    };
  }

  return component;
}

/**
 * Layout a stack container
 */
function layoutStack(
  stack: VStackComponent | HStackComponent,
  bounds: CalculatedBounds,
  context: LayoutContext,
): RenderedComponent[] {
  const rendered: RenderedComponent[] = [];

  const padding =
    typeof stack.padding === "number"
      ? {
          top: stack.padding,
          bottom: stack.padding,
          left: stack.padding,
          right: stack.padding,
        }
      : stack.padding || {};

  const contentBounds = {
    x: bounds.x + (padding.left || 0),
    y: bounds.y + (padding.top || 0),
    width: bounds.width - (padding.left || 0) - (padding.right || 0),
    height: bounds.height - (padding.top || 0) - (padding.bottom || 0),
  };

  // All children should already be filtered at this point
  const childSizes = stack.children.map((child) =>
    calculateComponentSize(child, context),
  );

  // Layout children
  for (let i = 0; i < stack.children.length; i++) {
    const child = stack.children[i];
    const childSize = childSizes[i];

    let childBounds: CalculatedBounds;

    if (stack.type === "vstack") {
      // Vertical stack - align children horizontally within the stack
      let alignmentOffset = 0;
      if (stack.alignment === "center") {
        alignmentOffset = Math.max(
          0,
          (contentBounds.width - childSize.width) / 2,
        );
      } else if (stack.alignment === "trailing") {
        alignmentOffset = Math.max(0, contentBounds.width - childSize.width);
      }
      // "leading" (default) keeps alignmentOffset = 0

      let yPosition: number;
      if (stack.spacing === "auto" && stack.children.length > 1) {
        // Auto spacing: first at top, last at bottom, others distributed evenly
        if (i === 0) {
          yPosition = contentBounds.y;
        } else if (i === stack.children.length - 1) {
          yPosition = contentBounds.y + contentBounds.height - childSize.height;
        } else {
          // For middle children, distribute them evenly in the remaining space
          const firstChildHeight = childSizes[0].height;
          const lastChildHeight = childSizes[stack.children.length - 1].height;
          const middleChildrenHeight = childSizes
            .slice(1, -1)
            .reduce((sum, s) => sum + s.height, 0);
          const availableSpace =
            contentBounds.height -
            firstChildHeight -
            lastChildHeight -
            middleChildrenHeight;
          const middleSpacing = availableSpace / (stack.children.length - 1);

          const previousHeight = childSizes
            .slice(0, i)
            .reduce((sum, s) => sum + s.height, 0);
          yPosition = contentBounds.y + previousHeight + middleSpacing * i;
        }
      } else {
        // Normal spacing
        yPosition = contentBounds.y;
        for (let j = 0; j < i; j++) {
          yPosition += childSizes[j].height;
          if (typeof stack.spacing === "number") {
            yPosition += stack.spacing;
          }
        }
      }

      childBounds = {
        x: contentBounds.x + alignmentOffset,
        y: yPosition,
        width: childSize.width,
        height: childSize.height,
      };
    } else {
      // Horizontal stack - align children vertically within the stack
      let alignmentOffset = 0;
      if (stack.alignment === "center") {
        alignmentOffset = Math.max(
          0,
          (contentBounds.height - childSize.height) / 2,
        );
      } else if (stack.alignment === "bottom") {
        alignmentOffset = Math.max(0, contentBounds.height - childSize.height);
      }
      // "top" (default) keeps alignmentOffset = 0

      let xPosition: number;

      // Simplified auto spacing - hardcode the 2-child case
      if (stack.spacing === "auto" && stack.children.length === 2) {
        if (i === 0) {
          // First child: left edge
          xPosition = contentBounds.x;
        } else {
          // Second child: right edge with safety buffer
          const safetyBuffer = 16; // Increased buffer for better spacing
          const minSpacing = 20; // Minimum space between components
          const leftChildEnd = contentBounds.x + childSizes[0].width;
          const rightEdgePosition =
            contentBounds.x +
            contentBounds.width -
            childSize.width -
            safetyBuffer;
          const minSpacingPosition = leftChildEnd + minSpacing;

          // Use the rightmost of: minimum spacing position or right edge position
          xPosition = Math.max(minSpacingPosition, rightEdgePosition);
        }
      } else {
        // Normal spacing (number or non-auto)
        xPosition = contentBounds.x;
        for (let j = 0; j < i; j++) {
          xPosition += childSizes[j].width;
          if (typeof stack.spacing === "number") {
            xPosition += stack.spacing;
          }
        }
      }

      childBounds = {
        x: xPosition,
        y: contentBounds.y + alignmentOffset,
        width: childSize.width,
        height: childSize.height,
      };
    }

    rendered.push(...layoutComponent(child, childBounds, context));
  }

  return rendered;
}

// ============================================================================
// Main Layout Engine API
// ============================================================================

/**
 * Render a layout definition to label fields
 */
export function renderLayout(
  layout: LayoutDefinition,
  labelSettings: LabelSettings,
  variables: Record<string, string> = {},
): LabelField[] {
  // Apply template variables first
  const interpolatedLayout = interpolateComponent(
    layout,
    variables,
  ) as LayoutDefinition;

  // Filter out empty components after interpolation
  const filteredLayout = filterEmptyComponents(
    interpolatedLayout,
  ) as LayoutDefinition | null;

  // If the entire layout is empty, return empty array
  if (!filteredLayout) {
    return [];
  }

  // Calculate available space (accounting for margins)
  const availableWidth =
    labelSettings.width -
    (labelSettings.marginLeft || 0) -
    (labelSettings.marginRight || 0);
  const availableHeight =
    labelSettings.height -
    (labelSettings.marginTop || 0) -
    (labelSettings.marginBottom || 0);

  const context: LayoutContext = {
    availableWidth,
    availableHeight,
    currentX: labelSettings.marginLeft || 0,
    currentY: labelSettings.marginTop || 0,
    labelSettings,
  };

  const rootBounds: CalculatedBounds = {
    x: context.currentX,
    y: context.currentY,
    width: availableWidth,
    height: availableHeight,
  };

  // Layout the filtered component
  const renderedComponents = layoutComponent(
    filteredLayout,
    rootBounds,
    context,
  );

  // Extract label fields
  return renderedComponents
    .map((c) => c.labelField)
    .filter((field): field is LabelField => field !== undefined);
}

/**
 * Render layout to ZPL code
 */
export function renderLayoutToZPL(
  layout: LayoutDefinition,
  labelSettings: LabelSettings,
  variables: Record<string, string> = {},
  options?: {
    printSpeed?: number;
    includeMargins?: boolean;
  },
): string {
  const fields = renderLayout(layout, labelSettings, variables);
  return generateZPL(labelSettings, fields, options);
}

// ============================================================================
// Debug Utilities
// ============================================================================

/**
 * Debug function to visualize layout bounds (useful for testing alignment)
 */
export function debugLayout(
  layout: LayoutDefinition,
  labelSettings: LabelSettings,
  variables: Record<string, string> = {},
): {
  fields: LabelField[];
  debugInfo: {
    totalBounds: CalculatedBounds;
    componentBounds: Array<{
      component: LayoutComponent;
      bounds: CalculatedBounds;
    }>;
  };
} {
  const interpolatedLayout = interpolateComponent(
    layout,
    variables,
  ) as LayoutDefinition;

  const availableWidth =
    labelSettings.width -
    (labelSettings.marginLeft || 0) -
    (labelSettings.marginRight || 0);
  const availableHeight =
    labelSettings.height -
    (labelSettings.marginTop || 0) -
    (labelSettings.marginBottom || 0);

  const context: LayoutContext = {
    availableWidth,
    availableHeight,
    currentX: labelSettings.marginLeft || 0,
    currentY: labelSettings.marginTop || 0,
    labelSettings,
  };

  const rootBounds: CalculatedBounds = {
    x: context.currentX,
    y: context.currentY,
    width: availableWidth,
    height: availableHeight,
  };

  const renderedComponents = layoutComponent(
    interpolatedLayout,
    rootBounds,
    context,
  );

  return {
    fields: renderedComponents
      .map((c) => c.labelField)
      .filter((field): field is LabelField => field !== undefined),
    debugInfo: {
      totalBounds: rootBounds,
      componentBounds: renderedComponents.map((c) => ({
        component: c.component,
        bounds: c.bounds,
      })),
    },
  };
}

// ============================================================================
// Custom Fields Utilities
// ============================================================================

/**
 * Extract custom fields that should be displayed on labels
 */
export function extractLabelFields(
  sample: LabelSample,
): Array<{ name: string; value: string }> {
  if (!sample.fields) return [];

  const labelFields: Array<{ name: string; value: string }> = [];

  for (const sampleField of sample.fields) {
    // Check if this field should be displayed on labels
    if (
      (sampleField.fieldSpec.displayOptions as Record<string, boolean>)
        ?.label !== true
    ) {
      continue;
    }

    // Get the actual value from the appropriate field
    let value: string | null = null;
    if (sampleField.textValue) {
      value = sampleField.textValue;
    } else if (sampleField.numericValue !== null) {
      // Handle both Decimal and number types
      value = sampleField.numericValue.toString();
    } else if (sampleField.option?.value) {
      value = sampleField.option.value;
    }

    // Only add if we have a value
    if (value) {
      labelFields.push({
        name: sampleField.fieldSpec.name,
        value: value,
      });
    }
  }

  return labelFields;
}

/**
 * Convert custom fields to template variables for the layout engine
 */
export function customFieldsToVariables(
  labelFields: Array<{ name: string; value: string }>,
): Record<string, string> {
  const variables: Record<string, string> = {};

  labelFields.slice(0, 3).forEach((field, index) => {
    variables[`customField${index + 1}`] = field.value;
  });

  return variables;
}

// ============================================================================
// Type Exports
// ============================================================================

export type {
  BarcodeComponent,
  BorderStyle,
  CalculatedBounds,
  HStackComponent,
  LayoutComponent,
  LayoutContext,
  LayoutDefinition,
  RenderedComponent,
  SpacerComponent,
  TextComponent,
  VStackComponent,
};
